import { ReactElement, useEffect, useState } from 'react';

import { useAppConfig } from 'hooks/appConfig';
import { useConversation } from 'hooks/conversation';
import { useAnalytics } from 'hooks/useAnalytics';
import { useView } from 'hooks/view';

import { IconClose } from 'components/Icons';
import NotificationBadge from './NotificationBadge';

import { DataDogLogger } from 'utils/dataDog';
import { CUSTOM_ACTION_NAMES } from 'utils/dataDog/constants';

import { ANALYTICS } from 'analytics/constants';
import { AnalyticClickedDetailsType, AnalyticReconnectedDetailsType } from 'analytics/types';
import { CHATUI_WINDOW_PATH } from 'constants/global';
import { ACTIONS } from 'constants/hook';
import { CHAT_UI_PREFIX } from 'constants/id';
import { htmlId } from 'utils/html';
import { Fab, StyledChatBubbleContainer, StyledChatUILogo } from './styles';

const ChatBubble = (): ReactElement | null => {
  const { appConfigState } = useAppConfig();
  const { viewState } = useView();
  const { conversationState, dispatchConversationAction } = useConversation();

  const { trackEvent } = useAnalytics();

  const [messagesSeenCount, setMessagesSeenCount] = useState(0);
  const conversationMessageCount = conversationState.messages.length;

  useEffect(() => {
    if (viewState.isPanelOpen) {
      setMessagesSeenCount(conversationMessageCount);
      if (conversationState.messagesUnreadCount > 0) {
        dispatchConversationAction({
          type: ACTIONS.CHANGE,
          payload: { messagesUnreadCount: 0 },
        });

        // Need  delay so latest messages can render their animations.
        // Otherwise the Twilio `lastReadMessageIndex` will be updated too quickly.
        const timeout = setTimeout(() => {
          conversationState.twilio?.conversation.setAllMessagesRead();
        }, 5000);

        return () => {
          clearTimeout(timeout);
        };
      }
    } else {
      if (conversationMessageCount > messagesSeenCount) {
        const nextMessagesUnreadCount = conversationState.messagesUnreadCount + 1;
        dispatchConversationAction({
          type: ACTIONS.CHANGE,
          payload: { messagesUnreadCount: nextMessagesUnreadCount },
        });
        if (appConfigState.isNativeMobileApp) {
          window?.webkit?.messageHandlers?.unreadCountChanged.postMessage('');
        }
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [conversationMessageCount, viewState.isPanelOpen]);

  const handleOnClick = async () => {
    const nextIsPanelOpen = !viewState.isPanelOpen;
    const needsInitialization =
      nextIsPanelOpen &&
      !conversationState.isConnected &&
      !conversationState.isInitializationRequested;
    let messages = conversationState.messages;

    if (nextIsPanelOpen) {
      window[CHATUI_WINDOW_PATH].show();
    } else {
      // prevent flash content reload when viewing chat again
      messages = await window[CHATUI_WINDOW_PATH].getMessages();
      window[CHATUI_WINDOW_PATH].hide();
    }
    if (needsInitialization && conversationState.isInactivityTimeout) {
      const context = { method: 'Chat bubble' };
      DataDogLogger.action(CUSTOM_ACTION_NAMES.INACTIVITY_RECONNECT, context);
      trackEvent({
        analyticEvent: ANALYTICS.EVENT.CHAT_RECONNECTED,
        details: { reconnect_method: context.method } as AnalyticReconnectedDetailsType,
      });
    }
    dispatchConversationAction({
      type: ACTIONS.CHANGE,
      payload: {
        messages,
        isAnimateLastMessage: false,
        isInitializationRequested: needsInitialization,
      },
    });
  };

  if (appConfigState.isNativeMobileApp) {
    // iOS app renders its own bubble element. But needs the unread message counter logic in this file's hook.
    return null;
  }

  return (
    <StyledChatBubbleContainer
      // NOTE: do not change ID below. Used in HTML query selectors by other teams
      id={htmlId('bubble')}
      onClick={(e) => {
        trackEvent({
          analyticEvent: viewState.isPanelOpen
            ? ANALYTICS.EVENT.CHAT_CLOSED
            : ANALYTICS.EVENT.CHAT_OPENED,
        });
        trackEvent({
          analyticEvent: ANALYTICS.EVENT.CLICKED,
          details: {
            analytic_click_id: `${CHAT_UI_PREFIX}-bubble`,
            content: '',
          } as AnalyticClickedDetailsType,
        });
        handleOnClick();
      }}
    >
      <Fab id={htmlId('bubble-fab')} size='xs'>
        {viewState.isPanelOpen ? (
          <IconClose
            id={htmlId('bubble-svg-close')}
            style={{ width: '1.5rem', height: '1.5rem' }}
          />
        ) : (
          <StyledChatUILogo id={htmlId('bubble-svg-open')} />
        )}
      </Fab>
      <NotificationBadge />
    </StyledChatBubbleContainer>
  );
};

export default ChatBubble;
